// shared/client-type.js — whether a client is a business or an individual.
// Pure: no imports, no DOM, no node builtins, so the server's validation, the
// client forms and the invoice drawer all read the same two values.
//
// It matters for one legal reason today: the Late Payment of Commercial Debts
// (Interest) Act 1998 covers debts between businesses, so statutory interest
// and fixed compensation can't be charged to an individual (see
// statutoryInterestApplies in ./statutory-interest.js).
//
// Existing clients are businesses (migration 067 defaults the column): a user
// who invoices only other businesses sees nothing change, and a homeowner is
// one click in the client form away from being marked an individual.

export const CLIENT_TYPES = ["business", "individual"];

export const CLIENT_TYPE_LABELS = {
  business: "Business",
  individual: "Individual"
};

export const DEFAULT_CLIENT_TYPE = "business";

// What the choice is CALLED and why it matters, so that the web's form and the
// app's ask it the same way. The hint is the reason the field exists at all:
// the type decides whether statutory late-payment interest can be claimed
// (`statutoryInterestApplies`), which is a legal difference and not a label.
export const CLIENT_TYPE_QUESTION = "Is this client a business or an individual?";
export const CLIENT_TYPE_HINT = "Statutory late-payment interest can only be claimed from a business.";

// Anything but an explicit "individual" is a business — the stored default,
// and what a payload from before the field existed means.
export function normaliseClientType(value) {
  return String(value || "").trim().toLowerCase() === "individual" ? "individual" : DEFAULT_CLIENT_TYPE;
}

// True only for a value the API accepts as given. Missing is fine (the caller
// keeps or defaults); a typo is not, so it can't silently become a business.
export function isValidClientType(value) {
  return CLIENT_TYPES.includes(String(value || "").trim().toLowerCase());
}

// The name field reads differently for each: a company has a legal name, a
// person just has a name.
export function clientNameCopy(clientType) {
  return normaliseClientType(clientType) === "individual"
    ? { label: "Name", hint: "The client’s full name.", missing: "Enter the client’s name." }
    : { label: "Company name", hint: "The full legal name of the company.", missing: "Enter the company name." };
}
